/** Build enriched MediaItems from raw S3 thumbnail listings. */
import type { MediaItem, S3File } from './types.ts';
import { kindFromKey } from './media.ts';
import {
  toOriginalKey,
  eventFromKey,
  displayName,
  timestampFromKey,
  isSidecarKey,
} from './keys.ts';

/**
 * Convert a thumbnail-listing S3File into a MediaItem.
 * The gallery lists the `thumb_uploads/` prefix, so `file.key` is the thumb key;
 * the original key (used as identity + for full-res viewing) is derived.
 */
export function thumbFileToItem(file: S3File): MediaItem {
  const originalKey = toOriginalKey(file.key);
  const lastModifiedMs = file.lastModified ? new Date(file.lastModified).getTime() : null;
  const fnameMs = timestampFromKey(file.key);
  return {
    key: originalKey,
    thumbnailKey: file.key,
    kind: kindFromKey(originalKey),
    event: eventFromKey(originalKey),
    displayName: displayName(originalKey),
    size: file.size,
    // Prefer filename timestamp (capture-ish) then lastModified.
    timestamp: fnameMs ?? (Number.isFinite(lastModifiedMs) ? lastModifiedMs : null),
  };
}

/** Map a listing into MediaItems, dropping sidecars and non-media entries. */
export function buildMediaItems(files: S3File[]): MediaItem[] {
  return files
    .filter((f) => !isSidecarKey(f.key))
    .map(thumbFileToItem)
    .filter((item) => item.kind !== 'other');
}
